import { randomId } from "@codeshare/protocol";
import type { HostMessage, WorkerMessage } from "./protocol";
import { createStdinBuffer, StdinWriter } from "./stdin";
import type {
  LoadProgress,
  OutputStream,
  RunExit,
  RunHandle,
  RunRequest,
  TestResult,
} from "./types";

export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  terminate(): void;
}

export interface StartRunOptions {
  /** Creates the runner's worker (one per run, so state never leaks between runs). */
  createWorker: () => WorkerLike;
  request: RunRequest;
  timeoutMs?: number;
  /** Override for tests; defaults to `crossOriginIsolated`. */
  stdinSupported?: boolean;
}

const encoder = new TextEncoder();

/** Runs one program in a fresh worker and exposes it as a RunHandle. */
export function startRun({
  createWorker,
  request,
  timeoutMs,
  stdinSupported,
}: StartRunOptions): RunHandle {
  const id = randomId();
  const canBlock =
    stdinSupported ??
    (typeof SharedArrayBuffer === "function" && Boolean(globalThis.crossOriginIsolated));
  const stdinBuffer = canBlock ? createStdinBuffer() : null;
  const stdin = stdinBuffer ? new StdinWriter(stdinBuffer) : undefined;
  const outputListeners = new Set<(stream: OutputStream, data: Uint8Array) => void>();
  const progressListeners = new Set<(progress: LoadProgress) => void>();
  const statusListeners = new Set<(message: string | null) => void>();
  const testListeners = new Set<(result: TestResult) => void>();
  const worker = createWorker();
  let settle!: (exit: RunExit) => void;
  let done = false;
  const exit = new Promise<RunExit>((resolve) => {
    settle = resolve;
  });

  const finish = (result: RunExit) => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    stdin?.dispose();
    worker.terminate();
    settle(result);
  };

  worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
    const message = event.data;
    switch (message.type) {
      case "output":
        for (const listener of outputListeners) listener(message.stream, message.data);
        break;
      case "progress":
        for (const listener of progressListeners) listener(message.progress);
        break;
      case "status":
        for (const listener of statusListeners) listener(message.message);
        break;
      case "test":
        for (const listener of testListeners) listener(message.result);
        break;
      case "exit":
        finish({ code: message.code, reason: "exit" });
        break;
      case "error":
        finish({ code: 1, reason: "error", message: message.message });
        break;
    }
  };
  worker.onerror = (event) =>
    finish({ code: 1, reason: "error", message: event.message || "worker failed" });

  const timer = timeoutMs
    ? setTimeout(() => finish({ code: 124, reason: "timeout" }), timeoutMs)
    : undefined;
  worker.postMessage({ type: "run", request, stdin: stdinBuffer } satisfies HostMessage);

  return {
    id,
    stdinSupported: canBlock,
    exit,
    onOutput(listener) {
      outputListeners.add(listener);
      return () => outputListeners.delete(listener);
    },
    onProgress(listener) {
      progressListeners.add(listener);
      return () => progressListeners.delete(listener);
    },
    onStatus(listener) {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },
    onTest(listener) {
      testListeners.add(listener);
      return () => testListeners.delete(listener);
    },
    writeStdin(data) {
      stdin?.write(typeof data === "string" ? encoder.encode(data) : data);
    },
    closeStdin() {
      stdin?.close();
    },
    resize(cols, rows) {
      if (!done) worker.postMessage({ type: "resize", cols, rows } satisfies HostMessage);
    },
    kill() {
      finish({ code: 130, reason: "killed" });
    },
  };
}
